import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from '../../../apiClient';

export type PagerDutyToken = {
  id: string;
  token: string;
  name: string;
  status: 'active' | 'error';
};

export type PagerDutyTokenForCreate = Omit<PagerDutyToken, 'id' | 'status'>;
export type PagerDutyTokenForList = Exclude<PagerDutyToken, 'token'>;

export const incidentsManagementPagerDutyTokensQueries = {
  all: () => ['incidents-management', 'pagerduty-tokens'],
  byProject: (projectId: string) => [
    ...incidentsManagementPagerDutyTokensQueries.all(),
    projectId,
  ],
  list: (projectId: string) =>
    queryOptions({
      queryKey: [
        ...incidentsManagementPagerDutyTokensQueries.byProject(projectId),
        'list',
      ],
      queryFn: async () => {
        const pagerDutyTokens = await listPagerDutyTokens(projectId);

        return pagerDutyTokens;
      },
    }),
  details: (projectId: string) => [
    ...incidentsManagementPagerDutyTokensQueries.byProject(projectId),
    'details',
  ],
  detail: (projectId: string, tokenId: string) =>
    queryOptions({
      queryKey: [
        ...incidentsManagementPagerDutyTokensQueries.details(projectId),
        tokenId,
      ],
      queryFn: async () => {
        const pagerDutyToken = await getPagerDutyToken(projectId, tokenId);

        return pagerDutyToken;
      },
    }),
} as const;

const listPagerDutyTokens = async (
  projectId: string,
): Promise<PagerDutyTokenForList[]> => {
  const getResponse = await fetchBackend(
    `/incidents-management/pagerduty/tokens/${projectId}`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    pagerDutyTokens: PagerDutyTokenForList[];
  };

  return data.pagerDutyTokens;
};

const getPagerDutyToken = async (
  projectId: string,
  tokenId: string,
): Promise<PagerDutyToken> => {
  const getResponse = await fetchBackend(
    `/incidents-management/pagerduty/tokens/${projectId}/${tokenId}`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    pagerDutyToken: PagerDutyToken;
  };

  return data.pagerDutyToken;
};

export const deletePagerDutyToken = async (
  projectId: string,
  tokenId: string,
): Promise<void> => {
  const deleteResponse = await fetchBackend(
    `/incidents-management/pagerduty/tokens/${projectId}/${tokenId}`,
    {
      method: 'DELETE',
    },
  );

  if (!deleteResponse.ok) {
    throw new Error(`Error: ${deleteResponse.statusText}`);
  }
};

export const upsertPagerDutyToken = async (
  projectId: string,
  payload: PagerDutyTokenForCreate,
  tokenId?: string,
): Promise<void> => {
  if (tokenId !== undefined) {
    const updateResponse = await fetchBackend(
      `/incidents-management/pagerduty/tokens/${projectId}/${tokenId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );

    if (!updateResponse.ok) {
      throw new Error(`Error: ${updateResponse.statusText}`);
    }
  }

  const createResponse = await fetchBackend(
    `/incidents-management/pagerduty/tokens/${projectId}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );

  if (!createResponse.ok) {
    throw new Error(`Error: ${createResponse.statusText}`);
  }
};
